import { FC, useContext, useEffect, useReducer, useRef, useState, useLayoutEffect } from "react";
import HighchartsReact from 'highcharts-react-official'
import { Link, useNavigate } from "react-router-dom";
import ReactPaginate from "react-paginate";

import AuthSubHeader from "src/components/authSubHeader";
import FooterText from "src/components/footerText";
import TableFilters from "src/components/caseFilters";
import RenderNextStep from "./caseItemActions";
import RequestDateOfTreatment from "src/components/modals/requestDateOfTreatment";
import CancelTreatment from "src/components/modals/cancelTreatment";

import useFetch from "src/hooks/useFetch";
import { APIEndpoints } from "src/types/apiTypes";
import { BAR_CHART } from "../../constants/highCharts";
import { ToastContext } from "src/context/toastContext";
import { ToastTypes } from "src/types";
import { AuthContext } from "src/context/authContext";
import { LoaderContext } from "src/context/loaderContext";

import { CaseListAPIResponseType, CaseListErrorResponse, DownloadZipResponseType, QueryParamsState, QueryStateActionType, QueryStateActions } from "./types";
import { CASE_HEADER_LISTING, CASE_STATUS_LISTING } from "src/constants/cases";
import { initialState, queryParamReducer } from "./reducer";
import { TreatmentStatusUpdateApiResponse } from "../treatmentListing/types";
import TreatedOrNotTreatedTreatment from "src/components/modals/treatedOrNotTreatedTreatment";
import { downloadZipFile, exportToExcel } from "src/helpers/downloadFile";
import {USER_TYPES} from "../../constants";
import AddSignaturePopUp from "../../components/modals/signatureModals/signaturePopUp";
import SignatureModal from "../../components/modals/signatureModals/addSignatureModal";
import AddSignatureButton from "../../components/addSignatureButton";



const Dashboard: FC = (): JSX.Element => {
  const [chartOptions, setChartOptions] = useState(BAR_CHART);
  const [caseTableLisitng, updateCaseTableListing] = useState<CaseListAPIResponseType>()
  const [queryParams, dispatch] = useReducer(queryParamReducer, initialState);
  const [showRequestTreatmentModal, setDataForRequestModalTreatment] = useState<{ isModalVisible: boolean; caseId: number; prescriptionId :number }>({
    isModalVisible: false,
    caseId: 0,
    prescriptionId:0
  });
  const [cancelTreatmentModal, setDataForCancelModal] = useState<{ isModalVisible: boolean; caseId: number; prescriptionId :number }>({
    isModalVisible: false,
    caseId: 0,
    prescriptionId:0
  });
  const [treatedOrNotTreatedTreatmentModal, setDataForTreatedOrNotTreatedTreatmentModal] = useState<{ isModalVisible: boolean,caseId:number,treatment_id:number}>({
    isModalVisible: false,
    caseId : 0,
    treatment_id: 0
  });
  const [isExportingInProgress, setExportingInProgressStatus] = useState<boolean>(false);

  const { showToast } = useContext(ToastContext);
  const { destroySession } = useContext(AuthContext);
  const { setLoader, hideLoader } = useContext(LoaderContext);
  const navigate = useNavigate();
  const chartComponentRef = useRef<HighchartsReact.RefObject>(null);
  const [selectedCaseStatus, setSelectedCaseStatus] = useState<string>("Active and Recently Completed Cases");
  const [showSignaturePrompt, setShowSignaturePrompt] = useState(false);
  const { userData } = useContext(AuthContext);
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [userSignature, setUserSignature] = useState <string| null>(null);
  const [hasPromptBeenShown, setHasPromptBeenShown] = useState(
      () => localStorage.getItem("signaturePromptShown") === "true"
  );



  const { data: caseObject, mutate: getCaseListing, error: caseError, removeErrorAndData: clearCaseState } =
    useFetch<CaseListAPIResponseType, QueryParamsState>(APIEndpoints.CASE_LIST, queryParams);

  useLayoutEffect(() => setLoader(), [])

  useEffect(() => {
    if (caseError) {
      hideLoader();
      const { code, error } = caseError as unknown as CaseListErrorResponse;
      showToast([error || `Can't fetch cases details right now. Please try again in a while.`], ToastTypes.ERROR);
      if (code === 401) {
        destroySession();
        navigate(`/users/sign_in`);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseError]);

  useEffect(() => {
    if (caseObject && caseObject.status) {
      hideLoader();
      if (caseObject.status === 500) {
        showToast(['No cases found against provided filters'], ToastTypes.ERROR);
      }

      if (queryParams.pagination) {
        updateCaseTableListing(caseObject)
        const latestChartOptions = { ...chartOptions };
        latestChartOptions.series = [{
          type: 'bar',
          name: 'Value',
          data: [caseObject.pending, caseObject.investigated],
        }];
  
        setChartOptions(latestChartOptions);
        const chart = chartComponentRef.current?.chart;
  
        if (chart) {
          chart.redraw();
        }
      } else {
        dispatch({
          type: QueryStateActions.CHANGE_PAGINATION_BIT,
          payload: true,
        })
        exportToExcel(caseObject.cases, "HCP Report List")
        setExportingInProgressStatus(false)
      }

    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseObject]);

  const onClearButtonClicked = () => {
    if (caseError || caseObject) clearCaseState();
    dispatch({
      type: QueryStateActions.CLEAR_STATE,
    });
    getCaseListing(null, null, initialState);
  };

  const handleSearchWithQueryParam = (updatedState?: QueryParamsState) => {
    if (caseError || caseObject) clearCaseState();
    setLoader();
    getCaseListing(null, null, updatedState ? updatedState : queryParams);
  };


  useEffect(() => {
    if (
        userData &&
        USER_TYPES.HCP.includes(userData.ref_type) &&
        userData.first_login_signature_check &&
        !hasPromptBeenShown
    ) {
      setShowSignaturePrompt(true);
      setHasPromptBeenShown(true);
      localStorage.setItem("signaturePromptShown", "true");
    }
  }, [userData, caseObject, hasPromptBeenShown]);

  const {data: signatureUploadResponse, error: signatureUploadError, mutate: uploadSignatureMutate, removeErrorAndData: clearSignatureState,
  } = useFetch<any>(APIEndpoints.UPDATE_SIGNATURE, {},{ id: userData?.id });

  const handleSaveSignature = async (file: File) => {
    clearSignatureState();
    const formData = new FormData();
    formData.append('global_doctor[signature_data]', file);
    formData.append('id', userData?.id?.toString() || '');
    formData.append('user',  'true');
    setUserSignature(URL.createObjectURL(file)); // show preview
    uploadSignatureMutate(formData);
    console.log("file", file)
  };

  useEffect(() => {
    if (signatureUploadResponse || signatureUploadError) {
      if (signatureUploadResponse.status.code === 200) {
        showToast(['Signature uploaded successfully'], ToastTypes.SUCCESS);
      } else {
        showToast(['Failed to upload signature'], ToastTypes.ERROR);
      }
    }
  }, [signatureUploadResponse, signatureUploadError]);

  const changeIdOrder = (value: string) => {
    const order = queryParams.direction === 'desc' ? 'asc' : 'desc';

    const action: QueryStateActionType = {
      type: QueryStateActions.CHANGE_SORT_PARAMS,
      payload: {
        direction: queryParams.sort_by === value ? order : "desc",
        sort_by: value,
      },
    };
    dispatch(action);

    const updatedState = queryParamReducer(queryParams, action);
    handleSearchWithQueryParam(updatedState);
  };

  const changePageNumber = (page: { selected: number; }) => {
    const action: QueryStateActionType = {
      type: QueryStateActions.CHANGE_PAGE,
      payload: page.selected+1,
    };
    dispatch(action);

    const updatedState = queryParamReducer(queryParams, action);
    handleSearchWithQueryParam(updatedState);
  };





  const { data: treatmentUpdateData, mutate: updatetreatment, error: treatmentUpdateError } =
    useFetch<TreatmentStatusUpdateApiResponse>(APIEndpoints.TREATMENT_STATUS_UPDATE);

  useEffect(() => {
    const hasUpdateData = treatmentUpdateData?.code;

    if (hasUpdateData || treatmentUpdateError) {
      if (hasUpdateData) {
        getCaseListing(null, null, initialState);
      }
      hideLoader();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [treatmentUpdateError, treatmentUpdateData]);

  const { data: downloadCaseData, loading: downloadCaseLoading, error: downloadCaseError, mutate: getDownloadLinkForZipFile, removeErrorAndData: clearZipFileData } =
    useFetch<DownloadZipResponseType>(APIEndpoints.DOWNLOAD_ZIP_FILE, {}, {}, false);

  useEffect(() => {
    if (downloadCaseData || downloadCaseError) {
      if(downloadCaseData){
        const { response,report_name } = downloadCaseData;
        downloadZipFile(response,report_name.replace(/.pdf/g, ".zip"));
      }
      hideLoader();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [downloadCaseData, downloadCaseError]);

  const downloadFileByCaseId = (caseId: number) => {
    if (downloadCaseData || downloadCaseError) clearZipFileData();
    setLoader();
    getDownloadLinkForZipFile({}, { caseId });
  };

  const onTreatedOrNotTreatedButtonClicked = (value: string, treatment_id: number) => {
    const requestBody = { status: 0 };

    if (value === "Not-Treated") {
      requestBody.status = 31;
    } else {
      requestBody.status = 22;
    }
    setLoader()
    updatetreatment(requestBody, { treatmentId: treatment_id });
  };


  const downloadExcelFile = () => {
    setExportingInProgressStatus(true)
    dispatch({
      type: QueryStateActions.CHANGE_PAGINATION_BIT,
      payload: false,
    })
    getCaseListing({}, {}, { ...queryParams, pagination: false })
  }




  return (
    <div className="container-fluid cases-page overflow-y-auto overflow-x-hidden d-flex flex-column flex-fill">
      <AuthSubHeader />

      <div className="card m-1 mt-4">
        <div className="card-header row g-0 justify-content-around">
          <h1 className="m-0 col-12 col-md-6">
            {selectedCaseStatus}
          </h1>
          <div className="col-12 col-md-6 text-end">
            {isExportingInProgress ? (
              <div className="col">
                <p className="text-dark h5">
                  <span className="spinner-border spinner-border-sm" style={{ width: 20, height: 20 }} aria-hidden="true"></span>{' '}
                  <i>Fetching HCP Report</i>
                </p>
              </div>
            ) : (
              <button type="button" className="btn btn-primary btn-sm" onClick={downloadExcelFile}>
                <i className="fas fa-file-download me-2"></i>Export
              </button>
            )}
          </div>
        </div>

        <div className="card-body p-0">
          <div className="card-body p-0">
            <div className="px-3 pt-3">
              <p>The following is a list of all active and completed cases. Click on the Case ID to view details of the
                case. Yellow cases require user attention.
              </p>

              <TableFilters
                  dropdownData={CASE_STATUS_LISTING}
                  inputPlaceholder="Search by Case ID, Patient, Prescriber"
                  state={queryParams}
                  dispatch={dispatch}
                  handleSearchWithQueryParam={handleSearchWithQueryParam}
                  onClearButtonClicked={onClearButtonClicked}
                  onStatusChange={setSelectedCaseStatus}
              />
            </div>

            <div className="row">
              <div className="col-1 offset-5" data-tour="first-step" style={{ maxHeight: 0, maxWidth: 0 }}></div>
            </div>

            <div className="table-responsive table-responsive-sm table-responsive-md table-responsive-lg">
              <table className="table table-striped table-sm mt-3" data-tour="second-step">
                <thead>
                <tr className="text-center">
                  {CASE_HEADER_LISTING.map((tableHeader, index) => (
                    <th
                      key={index} className="text-white" style={{ backgroundColor:' #0074BE'}}
                      onClick={tableHeader.value !== '' ? () => changeIdOrder(tableHeader.value) : undefined}
                    >
                      <span
                        role={tableHeader.value === '' ? "textbox" : "button"}
                        className={tableHeader.value === '' ?
                          '' :
                          'btn text-white btn-link link-underline link-underline-opacity-0 p-0 fw-bold'
                        }
                      >
                        {tableHeader.label}
                        {queryParams.sort_by === tableHeader.value ? (
                          <i className={`bi ${queryParams.direction === 'desc' ? 'bi-caret-down-fill' : 'bi-caret-up-fill'}`}></i>
                        ) : null}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {caseTableLisitng && caseTableLisitng.cases && caseTableLisitng.cases.length > 0 ?
                    caseTableLisitng.cases.map(kase => (
                        <tr key={kase.id} className="align-middle text-center">
                          {/* // orignal ID */}
                          <td>
                            <Link
                                to={{pathname: `/cases/detail/${kase.id}`}}
                                data-bs-toggle="tooltip"
                                data-bs-placement="right"
                                title={`Patient ID: ${kase.patient ? kase.patient.external_id : ''}`}
                            >
                              {kase.original_id}
                            </Link>
                           
                          </td>
                          {/* //review type  */}
                          <td>{kase ? kase.review_type && kase.review_type === "Quickstart" ? "Expedited" : kase.review_type : ''}</td>
                          {/* //created_at */}
                          <td>{kase ? kase.created_at : ''}</td>
                          {/* // investigated at */}
                          <td>{kase ? kase.investigated_at : ''}</td>
                           <td>{kase ? kase.drug_name : ''}</td>
                          {/* //rx_shipped */}
                          <td>{kase && kase.rx_shipped }</td>
                          {/* patient*/ }
                          <td>{kase && kase.patient ? kase.patient.firstlastname : ''}</td>
                          {/* physican*/ }
                          <td>{kase && kase.doctor ? kase.doctor.firstlastname : ''}</td>
                           {/* pharmacy*/ }
                          <td>{kase && kase.pharmacy}</td>
                          {/* status*/ }
                          <td className={`text-white align-middle ${kase.status_color || "bg-blue-lt"}`}>
                            {kase ? kase.computed_status : ''}
                          </td>
                          <td style={{minWidth:'176px'}}>
                            <RenderNextStep
                                kase={kase}
                                getCaseListing={getCaseListing}
                                showHideModalForTreatment={setDataForRequestModalTreatment}
                                setDataForCancelModal={setDataForCancelModal}
                                onTreatedOrNotTreatedButtonClicked={onTreatedOrNotTreatedButtonClicked}
                                setDataForTreatedOrNotTreatedTreatmentModal={setDataForTreatedOrNotTreatedTreatmentModal}
                            />
                          </td>
                          <td style={{minWidth:'370px'}}>
                            {kase.prescription_renewal &&
                                <Link to={`/cases/${kase ? kase.id : 0}/clone`} className="btn btn-success me-2">
                                  <i className="fas fa-sync-alt"></i> Prescription Renewal
                                </Link>
                            }
                          <button type="button" onClick={() => downloadFileByCaseId(kase.id)} disabled={downloadCaseLoading} className="btn btn-outline-secondary rounded-0">
                            <i className="fas fa-file-download"></i> Download
                          </button>
                          </td>
                        </tr>
                    ))
                    : null
                }
              </tbody>
            </table>
          </div>

          {caseTableLisitng && caseTableLisitng.total_pages ? (
            <ul className="pagination pagination-sm pagination-lg justify-content-center flex-wrap">
              {queryParams.page !== 1 ? (
                <li className="page-item">
                  <button
                    className="page-link"
                    onClick={() => changePageNumber({selected: 0})}
                  >
                    <i className="bi bi-chevron-double-left"></i> First
                  </button>
                </li>
              ) : null}

              <li>
                <ReactPaginate
                  nextLabel={<>Next <i className="bi bi-chevron-right"></i></>}
                  onPageChange={changePageNumber}
                  pageRangeDisplayed={3}
                  marginPagesDisplayed={2}
                  pageCount={caseTableLisitng.total_pages}
                  previousLabel={<><i className="bi bi-chevron-left"></i> Previous</>}
                  pageClassName="page-item"
                  pageLinkClassName="page-link"
                  previousClassName="page-item"
                  previousLinkClassName="page-link"
                  nextClassName="page-item"
                  nextLinkClassName="page-link"
                  breakLabel="..."
                  breakClassName="page-item"
                  breakLinkClassName="page-link"
                  containerClassName="pagination pagination-sm pagination-lg"
                  activeClassName="active"
                  renderOnZeroPageCount={null}
                  forcePage={queryParams.page - 1}
                />
              </li>

              {queryParams.page !== caseTableLisitng.total_pages ? (
                <li className={`page-item ${queryParams.page === caseTableLisitng.total_pages ? 'disabled' : ''}`}>
                  <button
                    className="page-link rounded-start-0"
                    onClick={() => changePageNumber({selected: caseTableLisitng.total_pages - 1})}
                  >
                    Last <i className="bi bi-chevron-double-right"></i>
                  </button>
                </li>
              ) : null}
            </ul>
          ) : null}
        </div>
      </div>

      <RequestDateOfTreatment
        isModalVisible={showRequestTreatmentModal.isModalVisible}
        caseId={showRequestTreatmentModal.caseId}
        prescriptionId={showRequestTreatmentModal.prescriptionId}
        hideModal={() => {
          setDataForRequestModalTreatment({
            isModalVisible: false,
            caseId: 0,
            prescriptionId: 0
          });
        }}
        refreshListing={() => {
          handleSearchWithQueryParam();
        }}
      />

      <CancelTreatment
        isModalVisible={cancelTreatmentModal.isModalVisible}
        caseId={cancelTreatmentModal.caseId}
        prescriptionId={cancelTreatmentModal.prescriptionId}
        hideModal={() => {
          setDataForCancelModal({
            isModalVisible: false,
            caseId: 0,
            prescriptionId: 0
          });
        }}
        refreshListing={() => {
          handleSearchWithQueryParam();
        }}
      />

      <TreatedOrNotTreatedTreatment
        isModalVisible={treatedOrNotTreatedTreatmentModal.isModalVisible}
        dataTreatmentStates={treatedOrNotTreatedTreatmentModal}
        onTreatedOrNotTreatedButtonClick={onTreatedOrNotTreatedButtonClicked}
        hideModal={() => {
          setDataForTreatedOrNotTreatedTreatmentModal({
            isModalVisible: false,
            caseId: 0,
            treatment_id: 0
          });
        }}
        refreshListing={() => {
          handleSearchWithQueryParam();
        }}
      />
        {/*{userData?.ref_type === "GlobalDoctor" && (*/}
        {/*    <AddSignatureButton*/}
        {/*        userSignature={userSignature}*/}
        {/*        onClick={() => setShowSignatureModal(true)}*/}
        {/*    />*/}
        {/*)}*/}
        {/* <AddSignaturePopUp
            isOpen={showSignaturePrompt}
            onClose={() => setShowSignaturePrompt(false)}
            onSignatureAdded={() => {
              setShowSignaturePrompt(false);
              setShowSignatureModal(true);
            }}
        />
        <SignatureModal
            isOpen={showSignatureModal}
            onClose={() => setShowSignatureModal(false)}
            onSave={handleSaveSignature}
        /> */}
      </div>
      <FooterText footerType="black"/>
    </div>
  );
};

export default Dashboard;
